import { InputText } from "primereact/inputtext";
import { FormEvent, useContext, useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import SocketContext from "../../../../../../socket/SocketProvider/SocketProvider";
import {
  IMessage,
  IMessagesResponse,
  RoomTypes,
} from "../../../../../../models/Chat";
import { useKeycloakContext } from "../../../../../../keycloak";
import { getInitialsFromName } from "../../../../../../helpers/helperFunctions";
import {
  EmployeeRoles,
  EmployeeRolesMapping,
} from "../../../../../../models/Employee";
import { If } from "../../../../../If/If";
import { ChatFileSelectPopup } from "../../../../../ChatFileSelectPopup/ChatFileSelectPopup";
import { getFileExtension } from "../../../../../../pages/Employee/EmployeeForm/helpers";
import imageCompression from "browser-image-compression";
import { Message } from "./Message/Message";

import classes from "./Messages.module.scss";
import { classNames as cx } from "primereact/utils";

const COUNT = 20;

interface IMessagesProps {
  groupId?: number;
  groupName?: string;
  participantsNames?: string;
  type?: RoomTypes;
  role?: string;
  newMessage: IMessage[];
}

export const Messages = ({
  groupId,
  groupName,
  participantsNames,
  type,
  role,
  newMessage,
}: IMessagesProps) => {
  const groupIdRef = useRef(groupId);

  const messagesDivRef = useRef<HTMLDivElement>(null);
  const messagesStartRef = useRef<number>(0);
  const totalMessagesRef = useRef<number>(0);

  const socket = useContext(SocketContext);
  const keycloakContext = useKeycloakContext();

  const frId = keycloakContext?.keycloak?.tokenParsed.frId;

  const [message, setMessage] = useState<string>("");
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [isOpenFileSelect, setIsOpenFileSelect] = useState<boolean>(false);

  useEffect(() => {
    if (newMessage.length) {
      setMessages(newMessage);
    }
  }, [newMessage]);

  useEffect(() => {
    if (!socket) {
      return;
    }

    socket.on("allMessages", (data: IMessagesResponse) => {
      totalMessagesRef.current = data.total;
      setMessages((prev: IMessage[]) => {
        return [...prev, ...data.result];
      });
    });

    return () => {
      socket.off("allMessages");
    };
  }, [socket]);

  const handleMessagesScroll = (e: Event) => {
    const scrollHeight = (e.target as HTMLDivElement).scrollHeight;
    const scrollTop = (e.target as HTMLDivElement).scrollTop;
    const clientHeight = (e.target as HTMLDivElement).clientHeight;

    // checks if scrolled to top - 20px
    if (scrollTop !== 0 && scrollHeight + scrollTop - clientHeight < 20) {
      messagesStartRef.current += COUNT;
      if (messagesStartRef.current < totalMessagesRef.current) {
        if (socket) {
          socket.emit("findAllMessages", {
            roomId: groupId,
            first: messagesStartRef.current,
            rows: COUNT,
          });
        }
      }
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!groupId) return;
    const trimmed = message.trim();
    if (trimmed && socket) {
      socket.emit("sendMessage", {
        roomId: groupId,
        text: trimmed,
        frId,
      });
      setMessage("");
    }
  };

  const handleFileSend = async (file: File) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    };

    const compressedFile = await imageCompression(file, options);

    if (file && socket) {
      socket.emit("sendFile", {
        roomId: groupId,
        text: compressedFile,
        frId,
        ext: getFileExtension(compressedFile.name),
      });
    }
  };

  useEffect(() => {
    groupIdRef.current = groupId;
    messagesStartRef.current = 0;
    setMessage("");
    if (socket && groupId) {
      socket.emit("findAllMessages", {
        roomId: groupId,
        first: messagesStartRef.current,
        rows: COUNT,
      });
      socket.on("messageSent", (data: IMessagesResponse) => {
        const lastMessage = data.result[0];
        if (lastMessage.roomId !== groupId) {
          return;
        }
        setMessages(data.result);
      });
    }
    messagesDivRef.current?.addEventListener("scroll", handleMessagesScroll);
    return () => {
      setMessages([]);
      if (socket) {
        socket.off("messageSent");
      }
      messagesDivRef.current?.removeEventListener(
        "scroll",
        handleMessagesScroll
      );
    };
  }, [groupId]);

  return (
    <div className={classes.messagesContainer}>
      <div className={classes.header}>
        <div className={classes.icon}>
          {type === RoomTypes.Group ? (
            <i className="pi pi-users" />
          ) : (
            <div className={classes.directIcon}>
              {participantsNames && getInitialsFromName(participantsNames)}
            </div>
          )}
        </div>
        <div>
          <div className={classes.groupName}>
            {type === RoomTypes.Group ? groupName : participantsNames}
          </div>
          <div className={classes.participants}>
            {type === RoomTypes.Group
              ? participantsNames
              : EmployeeRolesMapping[role as EmployeeRoles]}
          </div>
        </div>
      </div>
      <div className={classes.messages} ref={messagesDivRef}>
        {messages.length ? (
          [...messages].map((message: IMessage) => (
            <Message key={message.id} message={message} />
          ))
        ) : (
          <div className={classes.noMessages}>No messages to show</div>
        )}
      </div>
      <form className={classes.form} onSubmit={handleSubmit}>
        <Button
          icon="pi pi-paperclip"
          type="button"
          className={cx("p-button-text", classes.attachButton)}
          onClick={() => setIsOpenFileSelect(true)}
        />
        <InputText
          style={{ width: "100%" }}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={!groupId}
        />
        <Button
          style={{ width: "100px" }}
          label="Send"
          className="p-button-raised p-button-text"
          type="submit"
          disabled={!groupId}
        />
      </form>
      <If condition={isOpenFileSelect}>
        <ChatFileSelectPopup
          onSendFile={handleFileSend}
          onHide={() => setIsOpenFileSelect(false)}
        />
      </If>
    </div>
  );
};
